import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Smartphone as SmartphoneIcon, Wifi as WifiIcon, Clock as ClockIcon } from 'lucide-react'
import { api, type DeviceResponse, type ScheduleResponse } from '../lib/api'
import { queries } from '../lib/queries'
import { getDeviceStatus, formatDate } from '../lib/utils'
import DeviceModal from './DeviceModal'

const getDeviceType = (device: DeviceResponse) => (device.platform === 'ios' ? 'iOS Device' : 'Android Device')

export function DashboardClient() {
  const queryClient = useQueryClient()
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)

  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: queries.devices.list(),
    queryFn: () => api.getDevices(),
  })
  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: queries.schedules.list(),
    queryFn: () => api.getSchedules(),
  })

  const devices = devicesData?.devices ?? []
  const schedules = (schedulesData?.schedules ?? []).filter((s: ScheduleResponse) => s.isEnabled)

  const handleCloseDeviceModal = () => {
    setSelectedDeviceId(null)
    queryClient.invalidateQueries({ queryKey: queries.devices.list() })
  }

  return (
    <>
    {selectedDeviceId && (
      <DeviceModal deviceId={selectedDeviceId} onClose={handleCloseDeviceModal} />
    )}
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Devices Section */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Devices</h2>
            <Link to="/devices" className="text-sm text-brand hover:text-brand-hover font-medium">
              View all →
            </Link>
          </div>
          {devicesLoading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex-1" aria-busy="true">
              <div className="divide-y divide-gray-100">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="px-4 py-4 flex items-center gap-4 motion-safe:animate-pulse">
                    <div className="w-10 h-10 rounded-lg bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/3" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : devices.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center flex-1 flex flex-col items-center justify-center">
              <SmartphoneIcon size={48} className="text-gray-400 mb-3" />
              <p className="text-gray-500 mb-4">No devices yet</p>
              <Link
                to="/devices"
                search={{ add: true }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors"
              >
                Add Device
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex-1">
              <div className="divide-y divide-gray-100">
                {devices.map((device) => {
                  const status = getDeviceStatus(device.lastSeen, device.isEnrolled)
                  const isOnline = status === 'online'
                  return (
                    <button
                      key={device.id}
                      type="button"
                      onClick={() => setSelectedDeviceId(device.id)}
                      className="block w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                          <SmartphoneIcon size={20} className="text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-semibold text-gray-900 truncate">
                              {device.name || 'Unnamed Device'}
                            </h3>
                            {isOnline && <WifiIcon size={14} className="text-status-online shrink-0" />}
                          </div>
                          <p className="text-sm text-gray-500">
                            {getDeviceType(device)}
                            {device.lastSeen && (
                              <span className="ml-2">· Last active: {formatDate(device.lastSeen)}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Active Schedules Section */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Active Schedules</h2>
            <Link to="/schedules" className="text-sm text-brand hover:text-brand-hover font-medium">
              View all →
            </Link>
          </div>
          {schedulesLoading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex-1" aria-busy="true">
              <div className="divide-y divide-gray-100">
                {[1, 2].map((i) => (
                  <div key={i} className="px-4 py-4 flex items-start gap-4 motion-safe:animate-pulse">
                    <div className="w-10 h-10 rounded-lg bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-2/5" />
                      <div className="h-3 bg-gray-100 rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : schedules.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center flex-1 flex flex-col items-center justify-center">
              <ClockIcon size={48} className="text-gray-400 mb-3" />
              <p className="text-gray-500 mb-4">No active schedules</p>
              <Link
                to="/schedules"
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors"
              >
                Create Schedule
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex-1">
              <div className="divide-y divide-gray-100">
                {schedules.map((schedule) => (
                  <Link
                    key={schedule.id}
                    to="/schedules"
                    className="block px-4 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center shrink-0">
                        <ClockIcon size={16} className="text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 mb-1">{schedule.name}</h3>
                        <p className="text-sm text-gray-500 mb-2">{schedule.policy.name}</p>
                        {schedule.devices.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {schedule.devices.map((scheduleDevice) => (
                              <span
                                key={scheduleDevice.id}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                              >
                                <SmartphoneIcon size={12} className="text-gray-400" />
                                {scheduleDevice.device.name || 'Unnamed Device'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  )
}
